/** Leads (S30), a lead (S31) and call taps (S32). docs/50 §4.3, docs/52 §3.4. */
import { Button, Card, Chip, EmptyState, Filters, Icon, List, Row, type Tone } from '@ff/app-ui';
import type { leads as L } from '@ff/contracts';
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import type { z } from 'zod';
import { pathFor } from '../lib/api.ts';
import { invalidate, send, setCached, useQuery, useSend } from '../lib/data.ts';
import { dateLabel, dayShort, longDate, phoneLabel, rowTime, timeLabel } from '../lib/format.ts';
import { location, navigate, scoped } from '../lib/router.ts';
import { can, useReady } from '../lib/session.ts';
import { useStore } from '../lib/store.ts';
import { haptic, toast, tz } from '../lib/ui.ts';
import { S } from '@ff/app-ui/copy';
import { Info, Loaded, LoadError, Screen, Section } from '../ui/common.tsx';
import { Split } from '../ui/split.tsx';

type Filter = z.infer<typeof L.LeadFilter>;
type Lead = z.infer<typeof L.LeadDetail>;
type Status = Lead['status'];

const STATUS_TONE: Record<Status, Tone> = { new: 'new', contacted: 'neutral', booked: 'outline', won: 'ok', lost: 'muted', spam: 'muted' };

export function StatusChip({ status }: { status: Status }): JSX.Element {
  return <Chip tone={STATUS_TONE[status]} icon={status === 'won' ? 'check' : status === 'booked' ? 'calendar' : null}>{S.leads.status[status]}</Chip>;
}

export function LeadsScreen({ id }: { id?: string }): JSX.Element {
  const s = useReady();
  if (!can(s.membership, 'leads.read'))
    return (
      <Screen title={S.leads.title}>
        <EmptyState title={S.leads.title} body={S.leads.noPermission} />
      </Screen>
    );
  return <Split list={<LeadList selected={id} />} detail={id ? <LeadDetail id={id} /> : null} />;
}

function LeadList({ selected }: { selected?: string }): JSX.Element {
  const loc = useStore(location);
  const zone = useStore(tz);
  const [filter, setFilter] = useState<Filter>(((loc.query.get('filter') as Filter) ?? 'all') as Filter);
  const q = useQuery('leads', { query: { filter, ...(loc.scope ? { location: loc.scope } : {}) } });
  const counts = q.data?.counts;
  return (
    <Screen title={S.leads.title} query={q}>
      <Filters
        label={S.leads.filters}
        value={filter}
        onChange={(f) => {
          setFilter(f);
          history.replaceState(history.state, '', f === 'all' ? scoped('/leads', loc.scope) : `${scoped('/leads', loc.scope)}?filter=${f}`);
        }}
        options={(['all', 'new', 'calls', 'forms', 'won', 'filtered'] as Filter[]).map((f) => ({ value: f, label: S.leads.filter[f]!, count: f === 'new' ? (counts?.new ?? null) : f === 'filtered' ? (counts?.filtered || null) : null }))}
      />
      <Loaded q={q}>
        {(d) => (
          <div class="stack">
            {filter === 'filtered' && d.items.length > 0 && <p class="t-small t-muted">{S.leads.filteredNote}</p>}
            {d.callTaps.length > 0 && <CallTaps days={d.callTaps} />}
            {d.items.length > 0 ? (
              <List label={S.leads.title}>
                {d.items.map((l) => (
                  <li key={l.id}>
                    <Row
                      href={scoped(`/leads/${l.id}`, loc.scope)}
                      current={selected === l.id}
                      unread={l.unread}
                      unreadLabel={S.app.new}
                      title={l.name ?? S.leads.anon}
                      meta={[l.serviceLabel, l.town].filter(Boolean).join(' · ') || S.leads.source[l.source]}
                      time={rowTime(l.receivedAt, zone)}
                      chip={<StatusChip status={l.status} />}
                    />
                  </li>
                ))}
              </List>
            ) : filter === 'filtered' ? (
              <EmptyState title={S.leads.filteredEmpty} action={<Button variant="secondary" onClick={() => setFilter('all')}>{S.leads.showAll}</Button>} />
            ) : d.counts.total === 0 && filter === 'all' ? (
              <EmptyState title={S.leads.firstRunTitle} body={S.leads.firstRun} />
            ) : filter !== 'calls' ? (
              <EmptyState title={S.leads.emptyFilter} action={<Button variant="secondary" onClick={() => setFilter('all')}>{S.leads.showAll}</Button>} />
            ) : null}
            {d.counts.total > 0 && (
              <a class="section-link" href={pathFor('leadsCsv', {}, loc.scope ? { location: loc.scope } : undefined)} download="leads.csv">
                <Icon name="download" size={20} /> {S.leads.export}
              </a>
            )}
          </div>
        )}
      </Loaded>
    </Screen>
  );
}

function CallTaps({ days }: { days: z.infer<typeof L.CallTapDay>[] }): JSX.Element {
  const [today, ...rest] = days;
  if (!today) return <></>;
  return (
    <Card class="calltaps">
      <div class="card-head">
        <h2 class="t-heading">
          <Icon name="phone" size={22} /> {S.leads.callTapsTitle(today.count)}
        </h2>
        <Info title={S.leads.callTapsInfo[0]} lines={S.leads.callTapsInfo.slice(1)} />
      </div>
      <ul role="list" class="calltap-pages">
        {today.pages.map((p) => (
          <li key={p.path}>
            <span>{S.leads.fromPage(p.label.toLowerCase())}</span>
            <span class="t-data">{p.count}</span>
          </li>
        ))}
      </ul>
      {rest.map((d) => (
        <p key={d.day} class="t-small t-muted">
          {S.leads.callTapsOther(d.count, dayShort(d.day))}
        </p>
      ))}
    </Card>
  );
}

const STATUSES: Status[] = ['new', 'contacted', 'booked', 'won', 'lost'];

function LeadDetail({ id }: { id: string }): JSX.Element {
  const loc = useStore(location);
  const zone = useStore(tz);
  const q = useQuery('lead', { params: { id } });
  const job = useSend();
  if (q.error && !q.data) return <div class="screen"><LoadError error={q.error} onRetry={q.refetch} /></div>;
  const lead = q.data;
  const setStatus = async (status: Status, prev: Status) => {
    const out = await send('leadStatus', { params: { id }, body: { status } }).catch(() => null);
    if (!out) {
      toast(S.errors.offlineSend);
      return;
    }
    haptic();
    setCached('lead', { params: { id } }, () => out);
    invalidate('leads', 'home');
    toast(S.leads.statusSaved(S.leads.status[status]!.toLowerCase()), {
      label: S.leads.undo,
      run: () => void send('leadStatus', { params: { id }, body: { status: prev } }).then((o) => (setCached('lead', { params: { id } }, () => o), invalidate('leads'))),
    }, 5000);
  };
  return (
    <Screen title={lead?.name ?? S.leads.anon} back={scoped('/leads', loc.scope)} query={q}>
      <Loaded q={q} skeleton="detail">
        {(l) => (
          <div class="stack" data-gap="l">
            <div class="stack" data-gap="s">
              <p class="lead-meta">{[l.serviceLabel, l.town].filter(Boolean).join(' · ')}</p>
              <StatusChip status={l.status} />
            </div>

            <div class="contact-bar">
              {l.phone && (
                <Button variant="primary" size="lg" block href={`tel:${l.phone}`} icon="phone" external={false}>
                  {S.leads.callBack}
                  <span class="sr-only"> {phoneLabel(l.phone)}</span>
                </Button>
              )}
              <div class="btn-row" data-equal>
                {l.phone && (
                  <Button href={`sms:${l.phone}`} icon="sms">
                    {S.leads.text}
                  </Button>
                )}
                {l.email && (
                  <Button href={`mailto:${l.email}`} icon="mail">
                    {S.leads.email}
                  </Button>
                )}
                {l.town && (
                  <Button href={`https://maps.google.com/?q=${encodeURIComponent(l.town)}`} external icon="pin">
                    {S.leads.map}
                  </Button>
                )}
              </div>
              {l.phone && <p class="t-data t-muted contact-number">{phoneLabel(l.phone)}</p>}
            </div>

            <Card>
              {l.message ? <blockquote class="lead-message">{l.message}</blockquote> : <p class="t-muted">{S.leads.noMessage}</p>}
              <p class="t-small t-muted lead-came">
                {S.leads.cameIn(`${timeLabel(l.receivedAt, zone)}${rowTime(l.receivedAt, zone).includes(':') ? '' : `, ${dateLabel(l.receivedAt, zone)}`}`, l.pageLabel?.replace(/^\w/, (c) => c.toLowerCase()) ?? null)}
              </p>
            </Card>

            <Section title={S.leads.statusLabel} id="lead-status">
              {l.status === 'spam' ? (
                <Button variant="secondary" onClick={() => void setStatus('new', 'spam')}>
                  {S.leads.notSpam}
                </Button>
              ) : (
                <div class="status-grid" role="radiogroup" aria-label={S.leads.statusLabel}>
                  {STATUSES.map((st) => (
                    <button key={st} type="button" role="radio" aria-checked={l.status === st} class="status-opt" onClick={() => l.status !== st && void setStatus(st, l.status)}>
                      {l.status === st && <Icon name="check" size={18} />}
                      {S.leads.status[st]}
                    </button>
                  ))}
                </div>
              )}
            </Section>

            {l.canAskForReview && l.status === 'won' && (
              <Card class="jobdone">
                <p><strong>{S.leads.jobDone}</strong></p>
                <p class="t-muted">{S.leads.jobDoneLead}</p>
                <div class="card-foot">
                  <Button
                    variant="primary"
                    icon="star"
                    busy={job.busy}
                    onClick={() =>
                      void job.run(async () => {
                        const r = await send('leadJobDone', { params: { id } });
                        sessionStorage.setItem('ff-rr-prefill', JSON.stringify(r.prefill));
                        navigate(scoped('/reviews/ask', loc.scope));
                      })
                    }
                  >
                    {S.reviews.ask}
                  </Button>
                </div>
              </Card>
            )}

            <Section title={S.leads.delivery} id="lead-delivery" info={<Info title={S.leads.deliveryInfo[0]} lines={S.leads.deliveryInfo.slice(1)} />}>
              <ul role="list" class="deliveries">
                {l.deliveries.map((d) => (
                  <li key={d.id} data-status={d.status}>
                    <Icon name={d.status === 'failed' ? 'alert' : d.channel === 'push' ? 'bell' : d.channel === 'email' ? 'mail' : 'sms'} size={20} />
                    <span>{S.leads.deliveryRow(S.leads.channel[d.channel]!, d.recipientLabel, S.leads.deliveryStatus[d.status]!, timeLabel(d.deliveredAt ?? d.sentAt ?? d.queuedAt, zone))}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <p class="t-small t-muted">{S.leads.purge(longDate(l.purgeAfter, zone))}</p>
          </div>
        )}
      </Loaded>
    </Screen>
  );
}
